import { SAFE_ZONE_STRINGS } from '@src/safe-zones/strings';
import SpeakerIcon from '@src/svgs/SpeakerIcon';
import SpeakerMutedIcon from '@src/svgs/SpeakerMutedIcon';
import React from 'react';
import styled from 'styled-components';

import { useChromeFontSize } from '../useChromeFontSize';
import {
  OverlayFrame,
  OverlayMuteButton,
  useMuteToggle,
} from './sharedChrome';

import type { SafeZoneProps } from '@src/safe-zones/types';

const COPY = SAFE_ZONE_STRINGS.overlays.threadsFeedPost;

/**
 * Threads Feed Post overlay — paints only what overlaps a Threads-published
 * video on the in-feed surface: a small mute speaker in the bottom-right
 * corner. Everything else Threads renders around a video (author header,
 * action row, replies / likes counts, post text, scrubber, captions) lives
 * outside the video card on the real feed surface, so the safe-zone
 * preview paints none of it.
 *
 * Threads has no automatic / burned-in captions on the video itself — caption
 * text lives in the surrounding feed UI / overflow menu, not the rect — so
 * no captions variant ships either. Same for the play-controls variant: the
 * play / skip / time chrome lives outside the rect.
 *
 * Supports 16:9, 1:1, and 9:16 — the same single piece of chrome at every
 * aspect ratio, no per-AR layout branching.
 *
 * Scaling: chrome is sized in `em` against a base font-size derived from the
 * rect. Reference rect 510×309 → 1em = 10px. Scale factor
 * `min(rect.width / 510, rect.height / 309)` clamped to `[0.6, 3]`. Matches
 * LinkedIn Feed Post's scaling so the mute glyph reads at a consistent
 * physical size across overlays.
 */
const REFERENCE_WIDTH = 510;
const REFERENCE_HEIGHT = 309;
const REFERENCE_FONT_PX = 10;
const SCALE_MIN = 0.6;
const SCALE_MAX = 3;

// Higher muted-state opacity than Facebook / Instagram: the mute button is
// the only chrome Threads paints, so a dimmer muted state would read as
// "this overlay isn't drawing anything."
const MUTED_OPACITY = 0.85;

export const ThreadsFeedPostOverlay: React.FC<SafeZoneProps> = ({ rect }) => {
  const fontPx = useChromeFontSize(rect, {
    referenceWidth: REFERENCE_WIDTH,
    referenceHeight: REFERENCE_HEIGHT,
    referenceFontPx: REFERENCE_FONT_PX,
    scaleMin: SCALE_MIN,
    scaleMax: SCALE_MAX,
  });

  // Threads' in-feed video starts muted by default — autoplay is sound-off
  // until the viewer taps to unmute.
  const { muted, toggle } = useMuteToggle();

  return (
    <OverlayFrame
      data-testid="threads-feed-post-overlay"
      style={{ fontSize: `${fontPx}px` }}
    >
      <MuteButton
        type="button"
        onClick={toggle}
        aria-label={muted ? COPY.aria.unmute : COPY.aria.mute}
        aria-pressed={muted}
        $muted={muted}
        $mutedOpacity={MUTED_OPACITY}
      >
        {muted ? (
          <SpeakerMutedIcon
            width="1.2em"
            height="1.2em"
            fillColor="#ffffff"
            strokeColor="#ffffff"
          />
        ) : (
          <SpeakerIcon
            width="1.2em"
            height="1.2em"
            fillColor="#ffffff"
            strokeColor="#ffffff"
          />
        )}
      </MuteButton>
    </OverlayFrame>
  );
};

// ---------------------------------------------------------------------------
// Styles — all dimensions in `em` (1em = 10px @ 510×309 reference).
// ---------------------------------------------------------------------------

const MuteButton = styled(OverlayMuteButton)`
  right: 0.9em;
  bottom: 0.9em;
  width: 2em;
  height: 2em;
`;
